/**
 * Union-find over corner coordinates (prototype `buildUF`). Straight walls tie
 * coordinates together on one axis; measurements tie more. A coordinate group
 * moves as one, which is what keeps earlier measurements true.
 */
import { axisOf } from './geom';
import type { Axis, Corner, Pt, Room } from './types';

export interface UF {
  find(ax: Axis, id: number): number;
  same(ax: Axis, a: number, b: number): boolean;
}

export function buildUF(r: Room, withMeas = true): UF {
  const par: Record<Axis, Map<number, number>> = { x: new Map(), y: new Map() };
  r.corners.forEach((v) => { par.x.set(v.id, v.id); par.y.set(v.id, v.id); });
  const find = (ax: Axis, a: number): number => {
    const m = par[ax];
    if (!m.has(a)) return a;
    let x = a;
    while (m.get(x) !== x) { m.set(x, m.get(m.get(x)!)!); x = m.get(x)!; }
    return x;
  };
  const uni = (ax: Axis, a: number, b: number) => {
    if (!par[ax].has(a) || !par[ax].has(b)) return;
    const ra = find(ax, a), rb = find(ax, b);
    if (ra !== rb) par[ax].set(ra, rb);
  };
  const n = r.corners.length;
  for (let i = 0; i < n; i++) {
    const a = r.corners[i], b = r.corners[(i + 1) % n], t = axisOf(a, b);
    if (t === 'h') uni('y', a.id, b.id);
    else if (t === 'v') uni('x', a.id, b.id);
  }
  if (withMeas) r.measurements.forEach((m) => uni(m.axis, m.a, m.b));
  return { find, same: (ax, a, b) => find(ax, a) === find(ax, b) };
}

/** All corners in vid's coordinate group on axis ax. */
export function compOf(r: Room, U: UF, ax: Axis, vid: number): Corner[] {
  const root = U.find(ax, vid);
  return r.corners.filter((v) => U.find(ax, v.id) === root);
}

/** A wall is known (solid) when its length is fixed by the groups. */
export function edgeKnown(U: UF, a: Corner & Pt, b: Corner & Pt): boolean {
  const t = axisOf(a, b);
  if (t === 'h') return U.same('x', a.id, b.id);
  if (t === 'v') return U.same('y', a.id, b.id);
  return true;
}

/** Measurements still needed: (groups - 1) per axis. */
export function remaining(r: Room, U?: UF): number {
  const uf = U ?? buildUF(r);
  let n = 0;
  for (const ax of ['x', 'y'] as Axis[]) {
    const s = new Set(r.corners.map((v) => uf.find(ax, v.id)));
    n += Math.max(0, s.size - 1);
  }
  return n;
}

export function isMeasured(r: Room, U: UF, ax: Axis, vid: number): boolean {
  return r.measurements.some((m) => m.axis === ax && U.find(ax, m.a) === U.find(ax, vid));
}

/** Count of solid walls. */
export function knownCount(r: Room): number {
  const U = buildUF(r);
  const n = r.corners.length;
  return r.corners.filter((a, i) => edgeKnown(U, a, r.corners[(i + 1) % n])).length;
}
